/* eslint-disable no-unused-vars */
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import apis from '@src/apis';
import { isEqual } from 'lodash';
import Tabs from '@src/new-components/Tabs';
import {
  COMMUNITY_VOICES,
  VBEE_VOICES,
  COMMUNITY_SECTION,
  VOICE_SCOPE,
  COMMUNITY_SORT_TYPE,
  FAVOURITE_VOICES,
  FAVOURITE_VOICES_LIMIT,
} from '@src/constants/voiceLibrary';
import { useLocation, useHistory } from 'react-router-dom';
import {
  VOICE_FEATURED_PAGINATION_LIMIT,
  VOICE_OWNERSHIP,
  VOICE_TRENDING_PAGINATION_LIMIT,
  VOICE_NEWEST_PAGINATION_LIMIT,
  VOICE_LOAD_MORE_PAGINATION_LIMIT,
  VOICE_LOAD_MORE_PAGINATION_LIMIT_TRENDING,
  SORT_VOICE_OPTIONS_VBEE,
  DEFAULT_RETENTION_DAYS,
  SORT_VOICE_OPTIONS_COMMUNITY,
  SORT_VOICE_OPTIONS_FAVOURITE,
} from '@src/constants/voice';
import { isClonedVoiceCode } from '@vbee-holding/aivoice-shared-lib';
import { Box } from '@mui/material';
import { AddRounded } from '@mui/icons-material';
import Button from '@src/new-components/Button';
import Typography from '@src/new-components/Typography';
import ROUTES from '@src/constants/route';
import { CATEGORIES, VOICE_CLONING_TYPE } from '@src/constants/voiceCloning';
import { useTranslation } from 'react-i18next';
import i18n from '@src/languages';
import { useSelector } from 'react-redux';
import useHandleFavouriteVoices from '@src/hooks/useHandleFavouriteVoices';
import { StyledContentContainer } from './index.style';
import FeaturedVoice from './FeaturedVoice';
import Toolbar from '../Toolbar';
import VoiceResultHeader from './VoiceResultHeader';
import TrendingVoices from './TrendingVoices';
import NewestVoice from './NewestVoice';
import VoiceList from './VoiceList';

const TABS = [
  { label: VBEE_VOICES, name: VBEE_VOICES },
  { label: COMMUNITY_VOICES, name: COMMUNITY_VOICES },
  { label: FAVOURITE_VOICES, name: FAVOURITE_VOICES },
];

const INITIAL_DATA_FILTER = {
  language: { name: { vi: 'Tất cả', en: 'All' }, primaryLanguageSubTag: '' },
  region: '',
  sortBy: '',
  categories: [],
  advancedFilter: {
    ageGroup: [],
    gender: [],
    retentionDays: [],
    level: [],
  },
  search: '',
};

const CATEGORY_VALUES = CATEGORIES.map((category) => category.value);

// Educator Home ("Xem thêm") hands over a field filter through the router
// state. Seed it into the initial state so the very first fetch already
// carries it, instead of fetching empty and then refetching.
const buildInitialDataFilter = (categoriesFromState) => {
  const categories = (
    Array.isArray(categoriesFromState) ? categoriesFromState : []
  ).filter((category) => CATEGORY_VALUES.includes(category));
  if (!categories.length) return INITIAL_DATA_FILTER;

  return {
    ...INITIAL_DATA_FILTER,
    categories,
    // Match what picking the field by hand produces — handleChangeFilter
    // applies this same default whenever sortBy is still empty.
    sortBy: SORT_VOICE_OPTIONS_COMMUNITY[0].value,
  };
};

const INITIAL_COMMUNITY_VOICES = {
  featuredVoices: [],
  trendingVoices: [],
  newestVoices: [],
};

const SECTION_WITHOUT_TOOLBAR = [
  COMMUNITY_SECTION.FEATURED_VOICE,
  COMMUNITY_SECTION.TRENDING_VOICE,
];

const Content = ({ handleScrollToTop }) => {
  const { t } = useTranslation();
  const { language: currentLang } = i18n;
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const voiceCodeParam = searchParams.get('voice-code');
  const { state: stateLocation } = location;

  const [activeTab, setActiveTab] = useState(COMMUNITY_VOICES);
  const [previousEvent, setPreviousEvent] = useState(null);
  const [typeViewSection, setTypeViewSection] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextCursor, setNextCursor] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [voicesList, setVoicesList] = useState([]);
  const [favouriteVoicesList, setFavouriteVoicesList] = useState([]);
  const [favouriteVoiceCodes, setFavouriteVoiceCodes] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [searchValueView, setSearchValueView] = useState('');
  const [dataFilter, setDataFilter] = useState(() =>
    buildInitialDataFilter(stateLocation?.categories),
  );
  const [initialCommunityVoices, setInitialCommunityVoices] = useState(
    INITIAL_COMMUNITY_VOICES,
  );
  const [voiceCode, setVoiceCode] = useState(voiceCodeParam);
  const {
    globalCurrentFavouriteVoiceCodes,
    handleFavoriteToggleRequest,
    addToQueuePostProcessingFavouriteVoices,
    setGlobalCurrentFavouriteVoiceCodes,
    handleErrorProcessingFavouriteVoices,
  } = useHandleFavouriteVoices({ favouriteVoiceCodes });
  const isFirstLoadVoiceCode = useRef(true);
  const { user } = useSelector((state) => state.auth);

  const isCommunity = activeTab === COMMUNITY_VOICES;
  const isVbee = activeTab === VBEE_VOICES;
  const isFavourite = activeTab === FAVOURITE_VOICES;
  const controllerRef = useRef(null);

  const isViewingAllSection = SECTION_WITHOUT_TOOLBAR.includes(typeViewSection);
  const isViewingFilterResult =
    typeViewSection === COMMUNITY_SECTION.NEWEST_VOICE;

  const voicesListFinal = useMemo(() => {
    if (typeViewSection === COMMUNITY_SECTION.TRENDING_VOICE) {
      return voicesList.filter((voice) => voice?.stats?.totalCredits > 0);
    }
    return voicesList;
  }, [voicesList, typeViewSection]);

  const isFiltering = useMemo(
    () =>
      (dataFilter.sortBy && !isVbee && !isFavourite) ||
      (dataFilter.language && dataFilter.language?.primaryLanguageSubTag) ||
      dataFilter.region ||
      dataFilter.categories?.length > 0 ||
      dataFilter.advancedFilter?.ageGroup?.length > 0 ||
      dataFilter.advancedFilter?.gender?.length > 0 ||
      (dataFilter.advancedFilter?.retentionDays?.length > 0 &&
        dataFilter.advancedFilter?.retentionDays[0] !==
          DEFAULT_RETENTION_DAYS) ||
      dataFilter.advancedFilter?.level?.length > 0 ||
      dataFilter.search,
    [dataFilter, isVbee, isFavourite],
  );

  const isCommunityInitialView =
    isCommunity &&
    !isViewingAllSection &&
    !isViewingFilterResult &&
    !isFiltering &&
    !searchValueView;

  const hasButtonBack = isViewingAllSection && !isVbee;

  const getTitleResultHeader = useCallback(() => {
    const titles = {
      [COMMUNITY_SECTION.FEATURED_VOICE]: t('featuredVoice'),
      [COMMUNITY_SECTION.TRENDING_VOICE]: t('trendingVoice'),
    };
    if (titles[typeViewSection]) return titles[typeViewSection];
    if (isVbee && !isFiltering) return t('vbeeVoice');
    return t('result');
  }, [typeViewSection, isVbee, isFiltering, t]);

  const handleViewAllSection = useCallback((type) => {
    if (type === COMMUNITY_SECTION.NEWEST_VOICE) {
      setDataFilter((prev) => ({
        ...prev,
        sortBy: 'publishAt_desc',
      }));
      handleScrollToTop();
    }
    setTypeViewSection(type);
  }, []);

  const handleBackCommunityVoice = useCallback(() => {
    setTypeViewSection(null);
  }, []);

  useEffect(() => {
    const fetchLanguages = async () => {
      const data = await apis.language.getLanguages({
        voiceOwnership: isCommunity && VOICE_OWNERSHIP.COMMUNITY,
      });
      if (data?.status) setLanguages(data.result?.languages || []);
    };
    fetchLanguages();
  }, [isCommunity]);

  const fetchVoices = useCallback(
    (params, options) => apis.voices.getVoicesV3(params, options),
    [],
  );

  const paramsFetchVoices = useMemo(
    () => ({
      trendingVoice: {
        limit: VOICE_TRENDING_PAGINATION_LIMIT,
        voiceOwnership: VOICE_OWNERSHIP.COMMUNITY,
        sort: [
          COMMUNITY_SORT_TYPE.TRENDING_30_DAYS,
          COMMUNITY_SORT_TYPE.PUBLISH_AT_DESC,
        ].join(','),
      },
      featuredVoice: {
        limit: VOICE_FEATURED_PAGINATION_LIMIT,
        voiceOwnership: VOICE_OWNERSHIP.COMMUNITY,
        scope: VOICE_SCOPE.FEATURED,
        sort: [
          COMMUNITY_SORT_TYPE.TOTAL_CREDITS_DESC,
          COMMUNITY_SORT_TYPE.PUBLISH_AT_DESC,
        ].join(','),
      },
      newestVoice: {
        limit: VOICE_NEWEST_PAGINATION_LIMIT,
        voiceOwnership: VOICE_OWNERSHIP.COMMUNITY,
        sort: COMMUNITY_SORT_TYPE.PUBLISH_AT_DESC,
      },
      vbeeVoice: {
        voiceOwnership: VOICE_OWNERSHIP.VBEE,
      },
    }),
    [],
  );

  const fetchInitialCommunityVoices = useCallback(async () => {
    setIsInitialLoading(true);
    const [dataFeatured, dataTrending, dataNewest] = await Promise.all([
      fetchVoices(paramsFetchVoices.featuredVoice),
      fetchVoices(paramsFetchVoices.trendingVoice),
      fetchVoices(paramsFetchVoices.newestVoice),
    ]);
    if (dataFeatured?.status && dataTrending?.status && dataNewest?.status) {
      setInitialCommunityVoices({
        featuredVoices: dataFeatured.result?.voices || [],
        trendingVoices: dataTrending.result?.voices || [],
        newestVoices: dataNewest.result?.voices || [],
      });
    }
    setIsInitialLoading(false);
  }, [fetchVoices, paramsFetchVoices]);

  const fetchFavouriteVoices = useCallback(async () => {
    setIsLoading(true);
    const isVietnamese = dataFilter.language?.primaryLanguageSubTag === 'vi';
    const paramsFilterLanguage = {
      language: dataFilter.language?.primaryLanguageSubTag,
      ...(isVietnamese
        ? { locale: dataFilter?.region }
        : { region: dataFilter?.region }),
    };
    const paramsFilterCategory =
      dataFilter.categories?.length > 0
        ? { category: dataFilter.categories.join(',') }
        : {};
    const paramsFilterAdvancedFilter = {
      ageGroup: dataFilter.advancedFilter.ageGroup?.join(','),
      gender: dataFilter.advancedFilter.gender?.join(','),
    };

    // Check if the sortBy is valid for favourite voices
    // This is to prevent when user is in other tabs and then add/remove voice from favourite list
    // which will refetch the favourite voices list with the wrong sortBy.
    let sortBy;
    const isValidSortFavourite = SORT_VOICE_OPTIONS_FAVOURITE.some((option) =>
      isEqual(option.value, dataFilter.sortBy),
    );
    if (isValidSortFavourite) {
      sortBy = dataFilter.sortBy;
    } else {
      sortBy = SORT_VOICE_OPTIONS_FAVOURITE[0].value;
    }
    const paramsFilterSort = {
      sort: sortBy,
    };

    const data = await apis.favouriteVoices.getFavouriteVoices({
      ...paramsFilterLanguage,
      ...paramsFilterCategory,
      ...paramsFilterAdvancedFilter,
      ...paramsFilterSort,
      ...(dataFilter.search ? { search: dataFilter.search } : {}),
      ...(dataFilter.search
        ? { searchFields: 'name,code,description,category' }
        : {}),
      ...(dataFilter.search ? { lang: currentLang } : {}),
    });
    if (data?.status) {
      setFavouriteVoicesList(data.result?.favouriteVoices || []);
      setFavouriteVoiceCodes(
        data.result?.favouriteVoices?.map((voice) => voice.code) || [],
      );

      if (isInitialLoading) {
        setGlobalCurrentFavouriteVoiceCodes(
          data.result?.favouriteVoices?.map((voice) => voice.code) || [],
        );
      }
    }
    setIsLoading(false);
  }, [dataFilter, isInitialLoading]);

  const getListVoicesLoadMore = useCallback(
    async (isLoadMore = false) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      const isVietnamese = dataFilter.language?.primaryLanguageSubTag === 'vi';
      const paramsFilterLanguage = {
        language: dataFilter.language?.primaryLanguageSubTag,
        ...(isVietnamese
          ? { locale: dataFilter?.region }
          : { region: dataFilter?.region }),
      };
      const paramsFilterCategory =
        dataFilter.categories?.length > 0
          ? { category: dataFilter.categories.join(',') }
          : {};
      const paramsFilterAdvancedFilter = {
        ageGroup: dataFilter.advancedFilter.ageGroup?.join(','),
        gender: dataFilter.advancedFilter.gender?.join(','),
        retentionDays: dataFilter.advancedFilter.retentionDays?.join(','),
      };
      const paramsFilterLevel =
        dataFilter.advancedFilter.level?.length > 0
          ? { level: dataFilter.advancedFilter.level.join(',') }
          : {};

      setIsLoading(true);
      const data = await fetchVoices(
        {
          ...paramsFetchVoices[typeViewSection || 'vbeeVoice'],
          ...(isLoadMore && { cursor: nextCursor }),
          limit:
            typeViewSection === COMMUNITY_SECTION.TRENDING_VOICE
              ? VOICE_LOAD_MORE_PAGINATION_LIMIT_TRENDING
              : VOICE_LOAD_MORE_PAGINATION_LIMIT,
          ...(dataFilter.sortBy && { sort: dataFilter.sortBy }),
          voiceOwnership: isVbee
            ? VOICE_OWNERSHIP.VBEE
            : VOICE_OWNERSHIP.COMMUNITY,
          ...paramsFilterLanguage,
          ...paramsFilterCategory,
          ...paramsFilterAdvancedFilter,
          ...paramsFilterLevel,
          search: dataFilter.search,
          code: isFirstLoadVoiceCode.current ? voiceCode : '',
          ...(dataFilter.search && { lang: currentLang }),
        },
        { signal: controller.signal },
      );

      if (data?.status) {
        setHasNextPage(data.result?.pagination?.hasNextPage);
        setNextCursor(data.result?.pagination?.nextCursor);

        if (voiceCode && isFirstLoadVoiceCode.current) {
          setSearchValueView(voiceCode);
          setDataFilter((prev) => ({
            ...prev,
            search: voiceCode,
          }));
        }

        isFirstLoadVoiceCode.current = false;
        setVoiceCode(null);

        setVoicesList((prev) =>
          isLoadMore
            ? [...prev, ...(data.result?.voices || [])]
            : data.result?.voices || [],
        );
      }
      setIsLoading(false);
    },
    [
      dataFilter,
      fetchVoices,
      isVbee,
      nextCursor,
      paramsFetchVoices,
      typeViewSection,
      voiceCode,
      isFirstLoadVoiceCode.current,
    ],
  );

  const fetchLeaderboard = async () => {
    const data = await apis.leaderboard.getLeaderboard();
    if (data?.status) {
      setPreviousEvent(data.result.previousEvent);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    const getVoices = async () => {
      await fetchFavouriteVoices();
      await fetchInitialCommunityVoices();
    };

    getVoices();
  }, [fetchInitialCommunityVoices]);

  useEffect(() => {
    if (voiceCodeParam) {
      if (!isClonedVoiceCode(voiceCodeParam)) {
        setActiveTab(VBEE_VOICES);
        // Prevent double loading
        setIsInitialLoading(false);
      }
      setVoiceCode(voiceCodeParam);
    }
  }, [voiceCodeParam]);

  useEffect(() => {
    if (isFavourite) {
      setFavouriteVoicesList([]);
      setFavouriteVoiceCodes([]);
      fetchFavouriteVoices();
    } else {
      setVoicesList([]);
      setNextCursor('');
      setHasNextPage(false);
      getListVoicesLoadMore();
    }
  }, [typeViewSection, isVbee, dataFilter, isFavourite]);

  const resetDataFilter = useCallback((tab) => {
    let initSortBy = '';
    if (tab === VBEE_VOICES) {
      initSortBy = SORT_VOICE_OPTIONS_VBEE[0].value;
    } else if (tab === FAVOURITE_VOICES) {
      initSortBy = SORT_VOICE_OPTIONS_FAVOURITE[0].value;
    }
    setDataFilter({
      ...INITIAL_DATA_FILTER,
      sortBy: initSortBy,
    });
    setSearchValueView('');
  }, []);

  const handleChangeTab = useCallback(
    (tab) => {
      setActiveTab(tab);
      setTypeViewSection(null);
      resetDataFilter(tab);
    },
    [activeTab, resetDataFilter],
  );

  const dataLanguages = useMemo(() => {
    const allRegion = { name: { vi: 'Tất cả', en: 'All' }, regionSubTag: '' };
    const allLanguage = {
      name: { vi: 'Tất cả', en: 'All' },
      primaryLanguageSubTag: '',
    };

    return [allLanguage, ...languages].map((language) => {
      if (language?.primaryLanguageSubTag === 'vi') {
        return {
          ...language,
          regions: [
            allRegion,
            {
              name: { vi: 'Miền Bắc', en: 'Northern' },
              regionSubTag: 'northern',
            },
            {
              name: { vi: 'Miền Trung', en: 'Central' },
              regionSubTag: 'central',
            },
            {
              name: { vi: 'Miền Nam', en: 'Southern' },
              regionSubTag: 'southern',
            },
          ],
        };
      }
      return {
        ...language,
        regions: [allRegion, ...(language.regions || [])],
      };
    });
  }, [languages]);

  const handleChangeFilter = useCallback(
    (key, value) => {
      const initSortByCommunity =
        !isVbee &&
        value &&
        !dataFilter.sortBy &&
        SORT_VOICE_OPTIONS_COMMUNITY[0].value;

      if (key === 'language') {
        const selectedLanguage = dataLanguages.find(
          (lang) => lang.primaryLanguageSubTag === value,
        );
        setDataFilter((prev) => ({
          ...prev,
          region: '',
          language: selectedLanguage,
          ...(initSortByCommunity && {
            sortBy: initSortByCommunity,
          }),
        }));
      } else if (key === 'categories') {
        setDataFilter((prev) => {
          const categories = Array.isArray(prev.categories)
            ? [...prev.categories]
            : [];
          const idx = categories.indexOf(value);
          if (idx === -1) categories.push(value);
          else categories.splice(idx, 1);
          return {
            ...prev,
            ...(initSortByCommunity && { sortBy: initSortByCommunity }),
            categories,
          };
        });
      } else {
        setDataFilter((prev) => ({
          ...prev,
          ...(initSortByCommunity && { sortBy: initSortByCommunity }),
          [key]: value,
        }));
      }
    },
    [dataLanguages, isVbee, dataFilter.sortBy],
  );

  const shouldShowToolbar = SECTION_WITHOUT_TOOLBAR.includes(typeViewSection);

  const handleCreateVoice = () => {
    history.push({
      pathname: ROUTES.VOICE_CLONING_VOICES,
      state: { type: VOICE_CLONING_TYPE.CREATE },
    });
  };

  useEffect(() => {
    if (stateLocation?.highlightWeek) {
      handleViewAllSection(COMMUNITY_SECTION.NEWEST_VOICE);
      history.replace(ROUTES.VOICE_LIBRARY);
    }
  }, [stateLocation?.highlightWeek]);

  return (
    <StyledContentContainer>
      <div className="toolbar-container">
        <Box className="toolbar-container-tabs">
          <Tabs
            tabs={TABS}
            activeTab={activeTab}
            onChange={handleChangeTab}
            styleTab={{ padding: '10px 16px', marginBottom: '8px' }}
          />

          <Button
            variant="containedSmall"
            startIcon={<AddRounded sx={{ width: '24px', height: '24px' }} />}
            customStyle={{
              padding: '8px 16px',
              height: '40px',
              borderRadius: '8px',
              border: 'none',
            }}
            onClick={handleCreateVoice}
          >
            <Typography variant="body-14-semibold">
              {t('createVoice')}
            </Typography>
          </Button>
        </Box>

        {!shouldShowToolbar && (
          <Toolbar
            dataLanguages={dataLanguages}
            dataFilter={dataFilter}
            handleChangeFilter={handleChangeFilter}
            isVbee={isVbee}
            isFavourite={isFavourite}
            resetDataFilter={resetDataFilter}
            searchValueView={searchValueView}
            setSearchValueView={setSearchValueView}
            activeTab={activeTab}
          />
        )}
      </div>

      <>
        {/* Add voice code here to prevent flickering UI */}
        {!voiceCode && isCommunityInitialView && (
          <Box className="community-voices-container-first-show">
            <TrendingVoices
              onSeeAll={() =>
                handleViewAllSection(COMMUNITY_SECTION.TRENDING_VOICE)
              }
              voicesList={initialCommunityVoices.trendingVoices}
              section={COMMUNITY_SECTION.TRENDING_VOICE}
              isInitialLoading={isInitialLoading}
              isCommunity={isCommunity}
              onFetchFavouriteVoices={fetchFavouriteVoices}
              favouriteVoiceCodes={globalCurrentFavouriteVoiceCodes}
              onFavoriteToggleRequest={handleFavoriteToggleRequest}
              onErrorProcessingFavouriteVoices={
                handleErrorProcessingFavouriteVoices
              }
              onHandleFavoriteProcessingComplete={
                addToQueuePostProcessingFavouriteVoices
              }
              prizes={previousEvent?.prizes}
            />
          </Box>
        )}
        {!voiceCode && isCommunityInitialView && (
          <Box className="community-voices-container-first-show">
            <FeaturedVoice
              onSeeAll={() =>
                handleViewAllSection(COMMUNITY_SECTION.FEATURED_VOICE)
              }
              voicesList={initialCommunityVoices.featuredVoices}
              section={COMMUNITY_SECTION.FEATURED_VOICE}
              isInitialLoading={isInitialLoading}
              isCommunity={isCommunity}
              onFetchFavouriteVoices={fetchFavouriteVoices}
              favouriteVoiceCodes={globalCurrentFavouriteVoiceCodes}
              onFavoriteToggleRequest={handleFavoriteToggleRequest}
              onErrorProcessingFavouriteVoices={
                handleErrorProcessingFavouriteVoices
              }
              onHandleFavoriteProcessingComplete={
                addToQueuePostProcessingFavouriteVoices
              }
              prizes={previousEvent?.prizes}
            />
          </Box>
        )}
        {!voiceCode && isCommunityInitialView && (
          <NewestVoice
            onSeeAll={() =>
              handleViewAllSection(COMMUNITY_SECTION.NEWEST_VOICE)
            }
            voicesList={initialCommunityVoices.newestVoices}
            section={COMMUNITY_SECTION.NEWEST_VOICE}
            isInitialLoading={isInitialLoading}
            isCommunity={isCommunity}
            onFetchFavouriteVoices={fetchFavouriteVoices}
            favouriteVoiceCodes={globalCurrentFavouriteVoiceCodes}
            onFavoriteToggleRequest={handleFavoriteToggleRequest}
            onErrorProcessingFavouriteVoices={
              handleErrorProcessingFavouriteVoices
            }
            onHandleFavoriteProcessingComplete={
              addToQueuePostProcessingFavouriteVoices
            }
            prizes={previousEvent?.prizes}
          />
        )}

        {!isFavourite && (!isCommunityInitialView || isVbee) && (
          <Box className="voice-list-container-result">
            <VoiceResultHeader
              title={getTitleResultHeader()}
              onBack={handleBackCommunityVoice}
              hasBack={hasButtonBack}
            />
            <VoiceList
              voices={voicesListFinal}
              isCard={false}
              isLoadMore={!isCommunityInitialView || isVbee}
              hasMore={
                hasNextPage &&
                typeViewSection !== COMMUNITY_SECTION.TRENDING_VOICE
              }
              loadMore={() =>
                getListVoicesLoadMore(
                  typeViewSection !== COMMUNITY_SECTION.TRENDING_VOICE,
                )
              }
              isVbee={isVbee}
              isLoading={isLoading}
              isCommunity={isCommunity}
              isTrending
              onFetchFavouriteVoices={fetchFavouriteVoices}
              favouriteVoiceCodes={globalCurrentFavouriteVoiceCodes}
              onFavoriteToggleRequest={handleFavoriteToggleRequest}
              onErrorProcessingFavouriteVoices={
                handleErrorProcessingFavouriteVoices
              }
              onHandleFavoriteProcessingComplete={
                addToQueuePostProcessingFavouriteVoices
              }
              prizes={previousEvent?.prizes}
            />
          </Box>
        )}

        {isFavourite && (
          <Box className="voice-list-container-result">
            <VoiceList
              voices={favouriteVoicesList}
              isCard={false}
              isLoadMore
              hasMore={false}
              loadMore={() => {}}
              isVbee={isVbee}
              isLoading={isLoading}
              isCommunity={isCommunity}
              isFavourite={isFavourite}
              isTrending
              onFetchFavouriteVoices={fetchFavouriteVoices}
              favouriteVoiceCodes={favouriteVoiceCodes}
              onFavoriteToggleRequest={handleFavoriteToggleRequest}
              onErrorProcessingFavouriteVoices={
                handleErrorProcessingFavouriteVoices
              }
              onHandleFavoriteProcessingComplete={
                addToQueuePostProcessingFavouriteVoices
              }
              isFiltering={isFiltering}
              prizes={previousEvent?.prizes}
            />
          </Box>
        )}
      </>
    </StyledContentContainer>
  );
};

export default Content;
